import { _APP_CONFIG } from "../../core/config/AppConfig";
import { DataDRequest } from "../DRequest";
import type { IFinanzaAgrupadorCargos } from "../entidad/FinanzaAgrupadorCargos";
import { DataUtil } from "../util/Util";

/**
 * * Almacena solo los agrupadores consultados EnUso=true
 * * Usado para mostrar en listas asignables
 * * `Map<idEscuela, Map<ID, IFinanzaAgrupadorCargos>>`
 * */
export const _DICC_AGRUPADORES = new Map<number, Map<number, IFinanzaAgrupadorCargos>>();
/**
 * * Incluye todos los agrupadores consultados EnUso=true o false
 * * Usado para detalles
 * * `Map<idEscuela, Map<ID, IFinanzaAgrupadorCargos>>`
 * */
export const _DICC_AGRUPADORES_FULL = new Map<number, Map<number, IFinanzaAgrupadorCargos>>();
const PETICION_CACHE_DURATION_MS = 3 * 60 * 1000; // 3 minutos
const peticionCacheMap = new Map<number, { timestamp: number, data: IFinanzaAgrupadorCargos[] }>();

/** Consulta Agrupadores de cargos que se encuentran EnUso=true */
export function _SvFinanzasObtenerListaAgrupadorCargos(idEscuela: number, usarCache: boolean = false): Promise<DataDRequest.IRequestResponseA<IFinanzaAgrupadorCargos[]>> {
    // Verificar si hay datos en caché y si aún son válidos
    const cacheEntry = peticionCacheMap.get(idEscuela);
    const now = Date.now();
    if (usarCache && cacheEntry && cacheEntry.data.length > 0 && (now - cacheEntry.timestamp) < PETICION_CACHE_DURATION_MS) {
        return Promise.resolve({
            Resultado: 1,
            Datos: cacheEntry.data
        });
    }

    const url = _APP_CONFIG.URL_RSTV2 + "finanzas/ObtenerListaAgrupadorCargos";
    const params = {
        "IdEscuela": idEscuela,
    }
    return DataDRequest._Request5<IFinanzaAgrupadorCargos[]>(url, "GET", params).then(res => {
        if (res.Resultado > 0) {
            // Almacenar en caché los datos y la marca de tiempo
            peticionCacheMap.set(idEscuela, { timestamp: now, data: res.Datos });

            // Actualizar el diccionario de activos global
            const mapAgrupadores = new Map<number, IFinanzaAgrupadorCargos>();
            res.Datos.forEach(agrupador => {
                mapAgrupadores.set(agrupador.Id, agrupador);
            });
            _DICC_AGRUPADORES.set(idEscuela, mapAgrupadores);

            // Actualizar el diccionario completo global
            const mapAgrupadoresFull = _DICC_AGRUPADORES_FULL.get(idEscuela) || new Map<number, IFinanzaAgrupadorCargos>();
            res.Datos.forEach(agrupador => {
                mapAgrupadoresFull.set(agrupador.Id, agrupador);
            });
            _DICC_AGRUPADORES_FULL.set(idEscuela, mapAgrupadoresFull);
        }
        return res;
    })
}

/** Consulta Agrupadores de cargos por sus IDs, pueden estar EnUso=true o false */
export function _SvFinanzasObtenerListaAgrupadorCargosByIds(idEscuela: number, idsAgrupadores: number[]): Promise<DataDRequest.IRequestResponseA<IFinanzaAgrupadorCargos[]>> {
    const url = _APP_CONFIG.URL_RSTV2 + "finanzas/ObtenerListaAgrupadorCargos";
    const params = {
        "IdEscuela": idEscuela,
        "IdsAgrupadores": idsAgrupadores,
    }
    return DataDRequest._Request5<IFinanzaAgrupadorCargos[]>(url, "GET", params).then(res => {
        if (res.Resultado > 0) {
            // Actualizar el diccionario completo global
            const mapAgrupadoresFull = _DICC_AGRUPADORES_FULL.get(idEscuela) || new Map<number, IFinanzaAgrupadorCargos>();
            res.Datos.forEach(agrupador => {
                mapAgrupadoresFull.set(agrupador.Id, agrupador);
            });
            _DICC_AGRUPADORES_FULL.set(idEscuela, mapAgrupadoresFull);
        }
        return res;
    });
}

type IAgrupadorAgregar = Pick<IFinanzaAgrupadorCargos, "Prefix" | "IncluyeAlumno" | "IncluyePeriodo" | "ProdServSAT" | "UnidadSAT" | "ComplementoRequerido">;
export function _SvFinanzasNuevoAgrupadorCargos(idEscuela: number, agrupador: IAgrupadorAgregar): Promise<DataDRequest.IRequestResponseA> {
    const url = _APP_CONFIG.URL_RSTV2 + "finanzas/NuevoAgrupadorCargos";
    const params = {
        "IdUsuario": DataUtil._Usuario.IdUsuario,
        "IdEscuela": idEscuela,
        "Prefix": agrupador.Prefix,
        "IncluyeAlumno": agrupador.IncluyeAlumno,
        "IncluyePeriodo": agrupador.IncluyePeriodo,
        "ProdServSAT": agrupador.ProdServSAT,
        "UnidadSAT": agrupador.UnidadSAT,
        "ComplementoRequerido": agrupador.ComplementoRequerido,
    }
    return DataDRequest._Request5(url, "POST", params);
}

type IAgrupadorActualizar = Pick<IFinanzaAgrupadorCargos, "Id" | "Prefix" | "IncluyeAlumno" | "IncluyePeriodo" | "ProdServSAT" | "UnidadSAT" | "ComplementoRequerido">;
export type IAgrupadorActualizarResponse = DataDRequest.IRequestResponseA & { NCargosPlantillaModificados?: number };
export function _SvFinanzasActualizarAgrupadorCargos(agrupador: IAgrupadorActualizar): Promise<IAgrupadorActualizarResponse> {
    const url = _APP_CONFIG.URL_RSTV2 + "finanzas/ActualizarAgrupadorCargos";
    const params = {
        "IdUsuario": DataUtil._Usuario.IdUsuario,
        "IdAgrupador": agrupador.Id,
        "Prefix": agrupador.Prefix,
        "IncluyeAlumno": agrupador.IncluyeAlumno,
        "IncluyePeriodo": agrupador.IncluyePeriodo,
        "ProdServSAT": agrupador.ProdServSAT,
        "UnidadSAT": agrupador.UnidadSAT,
        "ComplementoRequerido": agrupador.ComplementoRequerido,
    }
    return DataDRequest._Request5<undefined, IAgrupadorActualizarResponse>(url, "POST", params);
}

export type IAgrupadorEliminarResponse = DataDRequest.IRequestResponseA & { NCargosPlantillaDesasignados?: number };
export function _SvFinanzasEliminarAgrupadorCargos(idAgrupador: number): Promise<IAgrupadorEliminarResponse> {
    const url = _APP_CONFIG.URL_RSTV2 + "finanzas/EliminarAgrupadorCargos";
    const params = {
        "IdUsuario": DataUtil._Usuario.IdUsuario,
        "IdAgrupador": idAgrupador,
    }
    return DataDRequest._Request5<undefined, IAgrupadorEliminarResponse>(url, "POST", params);
}

export type IAgrupadorAsignarCargosResponse = DataDRequest.IRequestResponseA & { NCargosAsignados?: number, NCargosDesasignados?: number };
export function _SvFinanzasAsignarCargosAgrupador(idAgrupador: number, idsCargosAsignar: number[], idsCargosRemover: number[]): Promise<IAgrupadorAsignarCargosResponse> {
    const url = _APP_CONFIG.URL_RSTV2 + "finanzas/AsignarAgrupadorCargos";
    const params = {
        "IdUsuario": DataUtil._Usuario.IdUsuario,
        "IdAgrupador": idAgrupador,
        "IdsCargosAsignar": idsCargosAsignar,
        "IdsCargosRemover": idsCargosRemover,
    }
    return DataDRequest._Request5<undefined, IAgrupadorAsignarCargosResponse>(url, "POST", params);
}